import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { LoaderCircle } from 'lucide-react';
import useGoogleSheetData from '../utility/useGoogleSheet';

const UserForm = ({ eventName, eventDate }) => {
  const { register, handleSubmit, control, formState: { errors }, setError, reset, clearErrors } = useForm();
  const [formStatus, setFormStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { data, loading, error } = useGoogleSheetData(
    '1IAAvkOLWu_a1RYnhd_fd8cLOl0GwhuYQw3a24IWxkqA',
    'countries'
  );

  const [countries, setCountries] = useState([]);

  useEffect(() => {
    if (data && data.length > 0) {
      const countryList = data[0].data.map((row) => Object.values(row)[0]);
      setCountries(countryList);
    }
  }, [data]);

  const onSubmit = async (formData) => {
    if (!formData.interest || formData.interest.length === 0) {
      setError('interest', {
        type: 'manual',
        message: 'Please select at least one option',
      });
      return;
    } else {
      clearErrors('interest');
    }

    const formattedData = {
      ...formData,
      eventName,
      eventDate: eventDate || '2024-07-03',
      interest: formData.interest.join(', '),
    };

    setIsLoading(true);
    try {
      const response = await fetch('/.netlify/functions/submitForm', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const result = await response.json();
      if (result && result.message) {
        setFormStatus({ success: true, message: result.message });
        reset();
      } else {
        throw new Error('Unexpected response structure: Missing message');
      }
    } catch (error) {
      setFormStatus({ success: false, message: error.message || 'Error submitting form' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} netlify>
      <div>
        <label htmlFor="firstName" className="form-label"><span>Your name</span><span className="text-red-500">*</span></label>
        <div className="grid grid-cols-2 gap-5">
          <div>
            <input {...register('firstName', { required: true })} className="form-input" placeholder="First" />
            {errors.firstName && <p className="text-red-500">First Name is required</p>}
          </div>
          <div>
            <input {...register('lastName', { required: true })} className="form-input" placeholder="Last" />
            {errors.lastName && <p className="text-red-500">Last Name is required</p>}
          </div>
        </div>
      </div>

      <div>
        <label htmlFor="email" className="form-label mt-3"><span>Email</span><span className="text-red-500">*</span></label>
        <input {...register('email', { required: true, pattern: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/ })} className="form-input" />
        {errors.email && <p className="text-red-500">Email is required and must be valid</p>}
      </div>

      <div>
        <label htmlFor="organisation" className="form-label mt-3"><span>Organisation</span></label>
        <input {...register('organisation')} className="form-input" />
      </div>

      <div>
        <label htmlFor="country" className="form-label mt-3"><span>Country</span><span className="text-red-500">*</span></label>
        {loading ? (
          <p>Loading countries...</p>
        ) : error ? (
          <p className="text-red-500">Error loading countries</p>
        ) : (
          <select {...register('country', { required: true })} className="form-input">
            <option value="">Select your country...</option>
            {countries.map((country, index) => (
              <option key={index} value={country}>
                {country}
              </option>
            ))}
          </select>
        )}
        {errors.country && <p className="text-red-500">Country is required</p>}
      </div>

      <div>
        <label htmlFor="interest" className="form-label mt-3"><span>I am interested in</span><span className="text-red-500">*</span></label>
        <Controller
          control={control}
          name="interest"
          defaultValue={[]}
          rules={{ validate: value => value.length > 0 || "Please select at least one option" }}
          render={({ field }) => (
            <div className="grid lg:grid-cols-3 gap-4">
              <div className="form-checkboxes">
                <label htmlFor="speaking" className="custom-checkbox-label">
                  <input
                    type="checkbox"
                    value="Speaking"
                    id="speaking"
                    className="custom-checkbox"
                    checked={field.value.includes('Speaking')}
                    onChange={(e) => {
                      const newValue = e.target.checked
                        ? [...field.value, e.target.value]
                        : field.value.filter(val => val !== e.target.value);
                      field.onChange(newValue);
                      if (newValue.length > 0) {
                        clearErrors('interest');
                      }
                    }}
                  />
                  Speaking/Presenting
                </label>
              </div>
              <div className="form-checkboxes">
                <label htmlFor="attending" className="custom-checkbox-label">
                  <input
                    type="checkbox"
                    value="Attending"
                    id="attending"
                    className="custom-checkbox"
                    checked={field.value.includes('Attending')}
                    onChange={(e) => {
                      const newValue = e.target.checked
                        ? [...field.value, e.target.value]
                        : field.value.filter(val => val !== e.target.value);
                      field.onChange(newValue);
                      if (newValue.length > 0) {
                        clearErrors('interest');
                      }
                    }}
                  />
                  Attending
                </label>
              </div>
              <div className="form-checkboxes">
                <label htmlFor="sponsoring" className="custom-checkbox-label">
                  <input
                    type="checkbox"
                    value="Sponsoring"
                    id="sponsoring"
                    className="custom-checkbox"
                    checked={field.value.includes('Sponsoring')}
                    onChange={(e) => {
                      const newValue = e.target.checked
                        ? [...field.value, e.target.value]
                        : field.value.filter(val => val !== e.target.value);
                      field.onChange(newValue);
                      if (newValue.length > 0) {
                        clearErrors('interest');
                      }
                    }}
                  />
                  Sponsoring
                </label>
              </div>
            </div>
          )}
        />
        {errors.interest && <p className="text-red-500">{errors.interest.message}</p>}
      </div>

      <button type="submit" className="form-button mt-5 flex flex-row gap-2 items-center" disabled={isLoading}>
        Submit 
        {isLoading && <LoaderCircle className="animate-spin" />}
      </button>

      {formStatus && (
        <p className={`mt-3 ${formStatus.success ? 'text-green-500' : 'text-red-500'}`}>
          {formStatus.message}
        </p>
      )}
    </form>
  );
};

export default UserForm;
